import { lazy, type ComponentType } from 'react';
import { isAppReloading } from './app-update';

type PageModule<T> = { default: T };

/** How long to wait for the reload before showing the error instead. */
export const RELOAD_WAIT_MS = 10_000;

/**
 * Waits for a page's code, for React.lazy. After a deploy an open tab can ask for a file that no
 * longer exists: index.tsx's vite:preloadError handler then starts a reload and prevents Vite's
 * error, so the import resolves to undefined. React.lazy would read `.default` of that and
 * show a crash before the reload takes over, so while a reload is under way this waits instead,
 * for at most RELOAD_WAIT_MS. Undefined with no reload under way, or a reload that never came,
 * throws an error reported as kind chunk.
 */
export async function loadPageModule<T>(load: () => Promise<PageModule<T>>): Promise<PageModule<T>> {
  const module = await load();
  if (module) return module;
  const missing = new Error('Failed to fetch dynamically imported module (it resolved to nothing)');
  if (!isAppReloading()) throw missing;
  // The reload can be cancelled (a "leave this page?" prompt), so don't wait forever.
  return new Promise<never>((_, reject) => setTimeout(() => reject(missing), RELOAD_WAIT_MS));
}

/** React.lazy for a page or chart's code that survives a deploy: `lazyPage(() => import('@/pages/home'))`. */
export function lazyPage<T extends ComponentType<any>>(load: () => Promise<PageModule<T>>) {
  // eslint-disable-next-line no-restricted-syntax -- the one place React.lazy is called.
  return lazy(() => loadPageModule(load));
}
